/**
 * Data Provider - API Integration
 *
 * Enterprise-grade data provider for API requests.
 *
 * @module DataProvider
 */

import { apiClient } from 'core/services/apiClient';
import logger from 'core/utils/logger';

/**
 * Data Provider Interface
 */
export interface DataProviderConfig {
  timeout?: number;
  headers?: Record<string, string>;
}

/**
 * Unified Data Provider
 */
export const dataProvider = {
  /**
   * GET request
   */
  get: async <T = any>(
    endpoint: string,
    params?: any,
    config?: DataProviderConfig
  ): Promise<T> => {
    logger.debug(`DataProvider GET: ${endpoint}`, { params });
    const result = await apiClient.get<T>(endpoint, { params, ...config });
    return result as T;
  },

  /**
   * POST request
   */
  post: async <T = any>(
    endpoint: string,
    data?: any,
    config?: DataProviderConfig
  ): Promise<T> => {
    logger.debug(`DataProvider POST: ${endpoint}`, { data });
    const result = await apiClient.post<T>(endpoint, data, config);
    return result as T;
  },

  /**
   * PUT request
   */
  put: async <T = any>(
    endpoint: string,
    data?: any,
    config?: DataProviderConfig
  ): Promise<T> => {
    logger.debug(`DataProvider PUT: ${endpoint}`, { data });
    const result = await apiClient.put<T>(endpoint, data, config);
    return result as T;
  },

  /**
   * PATCH request
   */
  patch: async <T = any>(
    endpoint: string,
    data?: any,
    config?: DataProviderConfig
  ): Promise<T> => {
    logger.debug(`DataProvider PATCH: ${endpoint}`, { data });
    const result = await apiClient.patch<T>(endpoint, data, config);
    return result as T;
  },

  /**
   * DELETE request
   */
  delete: async <T = any>(
    endpoint: string,
    config?: DataProviderConfig
  ): Promise<T> => {
    logger.debug(`DataProvider DELETE: ${endpoint}`);
    const result = await apiClient.delete<T>(endpoint, config);
    return result as T;
  },

  /**
   * Get data source info
   */
  getDataSourceInfo: () => ({
    mode: 'API',
    apiUrl: process.env.REACT_APP_API_URL,
  }),
};

// Log initialization
logger.info('DataProvider initialized', {
  mode: 'REAL API',
  apiUrl: process.env.REACT_APP_API_URL,
});

export default dataProvider;
